import { Component, type ErrorInfo, type ReactNode } from "react";

import { reportClientError } from "@/lib/api";
import { recordError } from "@/lib/telemetry";
import { isDexieStorageError, markStorageDegraded } from "@/lib/storageCapability";
import { Button } from "@/components/ui/button";

type Props = {
  children: ReactNode;
};

type State = {
  error: Error | null;
  /** Soft-recovered from a known storage/Dexie crash — show a one-shot notice. */
  storageSoftRecovered: boolean;
};

const SOFT_RECOVER_FLAG = "storage_soft_recovered";

function alreadySoftRecoveredThisSession(): boolean {
  try {
    return sessionStorage.getItem(SOFT_RECOVER_FLAG) === "1";
  } catch {
    return false;
  }
}

function markSoftRecoveredThisSession(): void {
  try {
    sessionStorage.setItem(SOFT_RECOVER_FLAG, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Catches render failures that would otherwise produce a blank root.
 * Offers reload + hard recovery (clear SW caches / local storage) for
 * stale PWA shells after deploys — a common blank-page cause.
 *
 * Dexie/IndexedDB failures under Safari Private browsing are soft-recovered
 * into online-only mode instead of a dead-end white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, storageSoftRecovered: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    try {
      reportClientError(error);
      recordError(error);
    } catch {
      // never throw from the boundary
    }
    if (info?.componentStack) {
      console.error("ErrorBoundary", error, info.componentStack);
    }

    // Known storage / Dexie Private-mode crashes: enter degraded mode and
    // soft-clear once so the dashboard can render in online-only mode.
    if (isDexieStorageError(error) && !alreadySoftRecoveredThisSession()) {
      markStorageDegraded(error.message || "dexie_storage_error");
      markSoftRecoveredThisSession();
      // Defer setState so we are outside the render/error phase.
      queueMicrotask(() => {
        this.setState({ error: null, storageSoftRecovered: true });
      });
    }
  }

  private reload = () => {
    window.location.reload();
  };

  private continueOnlineOnly = () => {
    markStorageDegraded(this.state.error?.message || "user_continued_online_only");
    markSoftRecoveredThisSession();
    this.setState({ error: null, storageSoftRecovered: true });
    try {
      window.history.replaceState(null, "", "/");
    } catch {
      /* ignore */
    }
  };

  private hardRecover = async () => {
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // Keep cookie auth; only clear client caches that commonly brick the shell.
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch {
        /* ignore */
      }
      try {
        // IndexedDB React Query / outbox shell (best-effort; full clear on next login)
        const dbs = await (indexedDB.databases?.() ?? Promise.resolve([]));
        await Promise.all(
          (dbs || [])
            .filter((d) => d?.name)
            .map(
              (d) =>
                new Promise<void>((resolve) => {
                  const req = indexedDB.deleteDatabase(d.name!);
                  req.onsuccess = req.onerror = req.onblocked = () => resolve();
                }),
            ),
        );
      } catch {
        /* ignore */
      }
    } catch (e) {
      console.warn("hardRecover partial failure", e);
    }
    window.location.href = "/login";
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }
    const msg = this.state.error?.message || "Unexpected error";
    const storageLike = isDexieStorageError(this.state.error);

    return (
      <div className="grid min-h-screen place-items-center bg-background p-6 text-foreground">
        <div className="max-w-md space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">
          <h1 className="text-lg font-semibold">
            {storageLike ? "Browser storage issue" : "Something went wrong"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {storageLike ? (
              <>
                Private browsing or restricted storage often causes this. You can continue in{" "}
                <strong>online-only</strong> mode (no offline queue), or clear cache and sign in again.
                Prefer normal Safari or Add to Home Screen for reliable offline punches.
              </>
            ) : (
              <>
                The app hit an unexpected error. This is often fixed by reloading after a deploy
                or clearing a stuck offline cache.
              </>
            )}
          </p>
          <p className="break-words rounded bg-muted px-2 py-1 font-mono text-xs text-muted-foreground">
            {msg}
          </p>
          <div className="flex flex-wrap gap-2">
            {storageLike && (
              <Button type="button" onClick={this.continueOnlineOnly}>
                Continue online-only
              </Button>
            )}
            <Button type="button" variant={storageLike ? "outline" : "default"} onClick={this.reload}>
              Reload
            </Button>
            <Button type="button" variant="outline" onClick={() => void this.hardRecover()}>
              Clear cache &amp; sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
